import { Check, Copy } from "lucide-react";
import React, { useEffect, useState } from "react";

function formatTs(
  t: { toDate?: () => Date; toMillis?: () => number } | null | undefined,
): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try {
    return t.toDate().toLocaleString();
  } catch {
    return "—";
  }
}

export type DynamicLinkDestinationUrlsProps = {
  androidUrl: string;
  iosUrl: string;
  fallbackUrl: string;
  createdAt:
    { toDate?: () => Date; toMillis?: () => number } | null | undefined;
  updatedAt:
    { toDate?: () => Date; toMillis?: () => number } | null | undefined;
};

type DestinationKey = "android" | "ios" | "fallback";

const ROWS: {
  key: DestinationKey;
  label: string;
  labelClass: string;
}[] = [
  {
    key: "android",
    label: "Android",
    labelClass: "font-extrabold text-[#58CC02]",
  },
  { key: "ios", label: "iOS", labelClass: "font-extrabold text-[#1CB0F6]" },
  {
    key: "fallback",
    label: "Fallback",
    labelClass: "font-extrabold text-[#FFC800]",
  },
];

const CopyButton = ({
  onClick,
  isCopied,
}: {
  onClick: () => void;
  isCopied: boolean;
}) => {
  return (
    <span className="mt-0.5 shrink-0 text-[#777777]" aria-hidden>
      {isCopied ? (
        <Check className="h-4 w-4 text-[#58CC02]" />
      ) : (
        <Copy className="h-4 w-4 cursor-pointer" onClick={onClick} />
      )}
    </span>
  );
};

export const DynamicLinkDestinationUrls: React.FC<
  DynamicLinkDestinationUrlsProps
> = ({ androidUrl, iosUrl, fallbackUrl, createdAt, updatedAt }) => {
  const [copiedKey, setCopiedKey] = useState<DestinationKey | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    if (!copiedKey) return;
    const id = window.setTimeout(() => setCopiedKey(null), 2000);
    return () => window.clearTimeout(id);
  }, [copiedKey]);

  useEffect(() => {
    if (!copyError) return;
    const id = window.setTimeout(() => setCopyError(null), 4000);
    return () => window.clearTimeout(id);
  }, [copyError]);

  const urlByKey: Record<DestinationKey, string> = {
    android: androidUrl,
    ios: iosUrl,
    fallback: fallbackUrl,
  };

  const handleCopy = async (key: DestinationKey) => {
    const text = urlByKey[key]?.trim() ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyError(null);
      setCopiedKey(key);
    } catch {
      setCopyError("Could not copy to clipboard.");
    }
  };

  return (
    <div className="space-y-3 border-t-2 border-[#E5E5E5] bg-[#F7F7F7] p-4 sm:p-5">
      <p className="text-xs font-extrabold uppercase tracking-wide text-[#777777]">
        Destination URLs
      </p>
      {copyError ? (
        <p className="text-xs font-bold text-[#B45309]" role="alert">
          {copyError}
        </p>
      ) : null}
      <dl className="space-y-2 text-sm">
        {ROWS.map(({ key, label, labelClass }) => {
          return (
            <div key={key}>
              <dt className={labelClass}>{label}</dt>
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 break-all font-mono font-semibold text-[#3C3C3C]">
                  {urlByKey[key]}
                </span>
                <CopyButton
                  onClick={() => handleCopy(key)}
                  isCopied={copiedKey === key}
                />
              </div>
            </div>
          );
        })}
        <div className="flex flex-wrap gap-4 pt-2 text-xs font-bold text-[#777777]">
          <span>Created {formatTs(createdAt)}</span>
          <span>Updated {formatTs(updatedAt)}</span>
        </div>
      </dl>
    </div>
  );
};
